import { useEffect, useId, useState, type ReactNode } from 'react'
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import LinkIcon from '@mui/icons-material/Link'
import { isSafeLinkHref, richTextPlainLength } from './richTextHelpers'

interface Props {
  /** Editor HTML (TipTap output). */
  value: string
  onChange: (html: string) => void
  label: string
  /** Plain-text character limit (markup excluded). */
  maxLength: number
  testIdPrefix: string
  error?: boolean
  helperText?: ReactNode
  disabled?: boolean
}

interface ToolbarAction {
  key: string
  label: string
  icon: ReactNode
  isActive: (editor: Editor) => boolean
  run: (editor: Editor) => void
}

const TOOLBAR_ACTIONS: ToolbarAction[] = [
  {
    key: 'bold',
    label: 'Bold',
    icon: <FormatBoldIcon fontSize="small" />,
    isActive: (e) => e.isActive('bold'),
    run: (e) => e.chain().focus().toggleBold().run(),
  },
  {
    key: 'italic',
    label: 'Italic',
    icon: <FormatItalicIcon fontSize="small" />,
    isActive: (e) => e.isActive('italic'),
    run: (e) => e.chain().focus().toggleItalic().run(),
  },
  {
    key: 'underline',
    label: 'Underline',
    icon: <FormatUnderlinedIcon fontSize="small" />,
    isActive: (e) => e.isActive('underline'),
    run: (e) => e.chain().focus().toggleUnderline().run(),
  },
  {
    key: 'bullet-list',
    label: 'Bulleted list',
    icon: <FormatListBulletedIcon fontSize="small" />,
    isActive: (e) => e.isActive('bulletList'),
    run: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    key: 'numbered-list',
    label: 'Numbered list',
    icon: <FormatListNumberedIcon fontSize="small" />,
    isActive: (e) => e.isActive('orderedList'),
    run: (e) => e.chain().focus().toggleOrderedList().run(),
  },
]

const toolbarButtonSx = (active: boolean) => ({
  borderRadius: '6px',
  color: active ? 'primary.main' : 'text.secondary',
  bgcolor: active ? 'action.selected' : 'transparent',
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
})

/**
 * BFP-1144 — TipTap rich text editor for distribution-channel email bodies.
 * Formatting is limited to bold, italic, underline, lists and links (matches the server sanitizer).
 */
export function NotificationRichTextEditor({
  value,
  onChange,
  label,
  maxLength,
  testIdPrefix,
  error = false,
  helperText,
  disabled = false,
}: Readonly<Props>) {
  const labelId = useId()
  const helperId = useId()
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkHref, setLinkHref] = useState('')
  const [linkError, setLinkError] = useState<string | null>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        trailingNode: false,
        link: {
          openOnClick: false,
          autolink: true,
          protocols: ['http', 'https', 'mailto'],
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        },
      }),
    ],
    content: value,
    editable: !disabled,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-labelledby': labelId,
        'aria-describedby': helperId,
        'data-testid': `${testIdPrefix}-input`,
      },
    },
    onUpdate: ({ editor: current }) => onChange(current.getHTML()),
  })

  const activeKeys = useEditorState({
    editor,
    selector: ({ editor: current }) =>
      current
        ? TOOLBAR_ACTIONS.filter((action) => action.isActive(current)).map((a) => a.key)
        : [],
  })

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [editor, value])

  useEffect(() => {
    if (editor?.isEditable === disabled) editor.setEditable(!disabled, false)
  }, [editor, disabled])

  const length = richTextPlainLength(value)
  const overLimit = length > maxLength

  const openLinkEditor = () => {
    setLinkHref(editor?.getAttributes('link').href ?? '')
    setLinkError(null)
    setLinkOpen(true)
  }

  const applyLink = () => {
    if (!editor) return
    const href = linkHref.trim()
    if (!isSafeLinkHref(href)) {
      setLinkError('Enter a link starting with https://, http:// or mailto:')
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    setLinkOpen(false)
  }

  const removeLink = () => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run()
    setLinkOpen(false)
  }

  return (
    <Box data-testid={testIdPrefix} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography id={labelId} component="span" sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
        {label}
      </Typography>
      <Box
        sx={{
          border: '1px solid',
          borderColor: error || overLimit ? 'error.main' : 'divider',
          borderRadius: '8px',
          bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
          '&:focus-within': { borderColor: error || overLimit ? 'error.main' : 'primary.main' },
        }}
      >
        <Box
          role="toolbar"
          aria-label={`${label} formatting`}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {TOOLBAR_ACTIONS.map((action) => {
            const active = activeKeys?.includes(action.key) ?? false
            return (
              <Tooltip key={action.key} title={action.label}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={action.label}
                    aria-pressed={active}
                    data-testid={`${testIdPrefix}-${action.key}`}
                    disabled={disabled || !editor}
                    onClick={() => editor && action.run(editor)}
                    sx={toolbarButtonSx(active)}
                  >
                    {action.icon}
                  </IconButton>
                </span>
              </Tooltip>
            )
          })}
          <Tooltip title="Link">
            <span>
              <IconButton
                size="small"
                aria-label="Link"
                aria-pressed={linkOpen}
                aria-expanded={linkOpen}
                data-testid={`${testIdPrefix}-link`}
                disabled={disabled || !editor}
                onClick={() => (linkOpen ? setLinkOpen(false) : openLinkEditor())}
                sx={toolbarButtonSx(linkOpen || Boolean(editor?.isActive('link')))}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {linkOpen ? (
          <Box
            data-testid={`${testIdPrefix}-link-panel`}
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1, px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <TextField
              size="small"
              fullWidth
              autoFocus
              label="Link URL"
              value={linkHref}
              error={Boolean(linkError)}
              helperText={linkError ?? undefined}
              onChange={(e) => setLinkHref(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  applyLink()
                }
              }}
              inputProps={{ 'data-testid': `${testIdPrefix}-link-input` }}
            />
            <Button size="small" variant="contained" onClick={applyLink} data-testid={`${testIdPrefix}-link-apply`} sx={{ textTransform: 'none', mt: 0.25 }}>
              Apply
            </Button>
            <Button size="small" onClick={removeLink} data-testid={`${testIdPrefix}-link-remove`} sx={{ textTransform: 'none', mt: 0.25 }}>
              Remove
            </Button>
          </Box>
        ) : null}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            minHeight: 120,
            fontSize: '14px',
            lineHeight: '20px',
            '& .ProseMirror': { outline: 'none', minHeight: 104 },
            '& .ProseMirror p': { m: 0 },
            '& .ProseMirror ul, & .ProseMirror ol': { my: 0.5, pl: 3 },
            '& .ProseMirror a': { color: 'primary.main', textDecoration: 'underline' },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <FormHelperText id={helperId} error={error || overLimit} sx={{ m: 0 }}>
          {helperText}
        </FormHelperText>
        <Typography
          component="span"
          data-testid={`${testIdPrefix}-counter`}
          aria-live="polite"
          sx={{ fontSize: '12px', color: overLimit ? 'error.main' : 'text.secondary', whiteSpace: 'nowrap' }}
        >
          {length}/{maxLength}
        </Typography>
      </Box>
    </Box>
  )
}
